/**
 * PostHog person properties for survey audience gating (QUI-186).
 */

export type PostHogUserType = 'writer' | 'producer' | 'enterprise';
export type PostHogPlan = 'per_analysis' | 'discovery_network' | 'enterprise';

export interface PostHogSurveyPersonSource {
  login_count?: number;
  analyses_completed?: number;
  signup_date?: string;
  user_type?: string;
  plan?: string;
  created_at?: string;
  roles?: string[];
  is_industry_partner?: boolean;
}

export interface PostHogSurveyPersonProperties {
  login_count: number;
  analyses_completed: number;
  signup_date: string;
  user_type: PostHogUserType;
  plan: PostHogPlan;
}

const USER_TYPES = new Set<PostHogUserType>(['writer', 'producer', 'enterprise']);
const PLANS = new Set<PostHogPlan>(['per_analysis', 'discovery_network', 'enterprise']);

function fallbackUserType(source: PostHogSurveyPersonSource): PostHogUserType {
  if (source.is_industry_partner) return 'enterprise';
  const roles = (source.roles ?? []).map((r) => r.trim().toLowerCase());
  if (roles.some((r) => r === 'producer' || r === 'executive')) return 'producer';
  return 'writer';
}

function fallbackSignupDate(source: PostHogSurveyPersonSource): string | null {
  if (source.signup_date) return source.signup_date;
  if (!source.created_at) return null;
  return source.created_at.split('T')[0] ?? null;
}

/** Build survey person properties from `/api/auth/me` (with client fallbacks). */
export function buildPostHogSurveyPersonProperties(
  source: PostHogSurveyPersonSource,
): PostHogSurveyPersonProperties | null {
  const signupDate = fallbackSignupDate(source);
  if (!signupDate) return null;

  const userTypeRaw = source.user_type?.toLowerCase();
  const planRaw = source.plan?.toLowerCase();

  return {
    login_count: source.login_count ?? 0,
    analyses_completed: source.analyses_completed ?? 0,
    signup_date: signupDate,
    user_type: USER_TYPES.has(userTypeRaw as PostHogUserType)
      ? (userTypeRaw as PostHogUserType)
      : fallbackUserType(source),
    plan: PLANS.has(planRaw as PostHogPlan)
      ? (planRaw as PostHogPlan)
      : 'per_analysis',
  };
}
